(() => {
  if (window.__snugMobileSearchStandaloneLoaded) return;
  window.__snugMobileSearchStandaloneLoaded = true;
  window.__snugStandaloneMobileSearch = true;

  const MOBILE_QUERY = '(max-width: 768px)';
  const mobileMedia = window.matchMedia(MOBILE_QUERY);
  const root = document.documentElement;
  const TRIGGER_SELECTOR = [
    'header-search .search__button',
    'header-search .top-search-toggle',
    'header-search [data-action="toggle-search"]',
    'header-search button[aria-label*="looking" i]',
    'header-search button[aria-label*="search" i]',
    '.header__icon--search',
    '[data-action="toggle-search"]',
    'a[href="/search"]',
  ].join(', ');

  let sheet = null;
  let panel = null;
  let input = null;
  let results = null;
  let status = null;
  let debounceTimer = null;
  let requestController = null;
  let lastTrigger = null;
  let openedAt = 0;

  const isMobile = () => mobileMedia.matches;

  const normalizeSearchQuery = (value) => {
    const term = value.trim();
    return /^(sofa|couch) covers?$/i.test(term) ? `"${term}"` : term;
  };

  const closeNativeSearch = () => {
    document.querySelectorAll('header-search .popup-search-form').forEach((popup) => {
      popup.classList.remove('popup-search-show');
      popup.classList.add('search-hidden');
      popup.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('header-search input[type="search"]').forEach((nativeInput) => {
      nativeInput.setAttribute('aria-expanded', 'false');
      nativeInput.blur();
    });
    root.classList.remove('open-search', 'open-drawer', 'snug-search-open');
    document.body.classList.remove('snug-search-open');
  };

  const closeCartBeforeSearch = () => {
    document.querySelectorAll('.minicart__wrapper.open').forEach((drawer) => {
      drawer.classList.remove('open');
      drawer.classList.add('invisible');
    });
    root.classList.remove('open-minicart', 'open-drawer');
    document.body.classList.remove('open-minicart', 'open-drawer');
  };

  const formatPrice = (product) => {
    const raw = product.price_min ?? product.price;
    let amount = Number(raw);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    if (amount >= 1000) amount /= 100;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const safeProductUrl = (value) => {
    try {
      const url = new URL(value || '', window.location.origin);
      return url.origin === window.location.origin && url.pathname.startsWith('/products/') ? url.href : '';
    } catch (_) {
      return '';
    }
  };

  const createProductCard = ({ title, url, image, price }) => {
    const href = safeProductUrl(url);
    if (!href || !title) return null;

    const card = document.createElement('a');
    card.className = 'snug-mobile-search-product';
    card.href = href;

    const media = document.createElement('span');
    media.className = 'snug-mobile-search-product__media';
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 88;
      img.height = 104;
      media.appendChild(img);
    }

    const copy = document.createElement('span');
    copy.className = 'snug-mobile-search-product__copy';
    const name = document.createElement('strong');
    name.textContent = title;
    copy.appendChild(name);
    if (price) {
      const priceNode = document.createElement('span');
      priceNode.className = 'snug-mobile-search-product__price';
      priceNode.textContent = price;
      copy.appendChild(priceNode);
    }
    const action = document.createElement('span');
    action.className = 'snug-mobile-search-product__action';
    action.textContent = 'View product →';
    copy.appendChild(action);

    card.append(media, copy);
    return card;
  };

  const setStatus = (message) => {
    if (!status) return;
    status.textContent = message;
  };

  const renderDefault = () => {
    if (!results) return;
    results.replaceChildren();
    setStatus('');

    const trending = document.createElement('section');
    trending.className = 'snug-mobile-search-default';
    const trendingTitle = document.createElement('h2');
    trendingTitle.textContent = 'Popular searches';
    const chips = document.createElement('div');
    chips.className = 'snug-mobile-search-chips';
    [
      ['Sofa covers', '/search?q=%22sofa+cover%22&type=product'],
      ['Pet-friendly covers', '/search?q=%22pet-friendly+sofa+cover%22&type=product'],
      ['Chenille covers', '/search?q=%22chenille+sofa+cover%22&type=product'],
      ['Cushion covers', '/search?q=cushion+cover&type=product'],
    ].forEach(([label, href]) => {
      const chip = document.createElement('a');
      chip.href = href;
      chip.textContent = label;
      chips.appendChild(chip);
    });
    trending.append(trendingTitle, chips);
    results.appendChild(trending);

    const sourceCards = Array.from(document.querySelectorAll('header-search .product-item')).slice(0, 4);
    const popularCards = sourceCards.map((source) => {
      const link = source.querySelector('a[href*="/products/"]');
      const title = source.querySelector('.product-item__name')?.textContent?.trim() || link?.getAttribute('aria-label')?.trim();
      const image = source.querySelector('img')?.currentSrc || source.querySelector('img')?.src || '';
      const price = source.querySelector('.product-item__price')?.textContent?.replace(/\s+/g, ' ')?.trim() || '';
      return createProductCard({ title, url: link?.href, image, price });
    }).filter(Boolean);

    if (popularCards.length) {
      const popular = document.createElement('section');
      popular.className = 'snug-mobile-search-popular';
      const popularTitle = document.createElement('h2');
      popularTitle.textContent = 'Popular products';
      const grid = document.createElement('div');
      grid.className = 'snug-mobile-search-products';
      popularCards.forEach((card) => grid.appendChild(card));
      popular.append(popularTitle, grid);
      results.appendChild(popular);
    }
  };

  const renderProducts = (products, query) => {
    results.replaceChildren();
    const grid = document.createElement('div');
    grid.className = 'snug-mobile-search-products';

    products.forEach((product) => {
      const image = typeof product.image === 'string'
        ? product.image
        : product.image?.url || product.featured_image?.url || product.featured_image || '';
      const card = createProductCard({
        title: product.title,
        url: product.url,
        image,
        price: formatPrice(product),
      });
      if (card) grid.appendChild(card);
    });

    if (grid.children.length) {
      results.appendChild(grid);
      setStatus(`${grid.children.length} product${grid.children.length === 1 ? '' : 's'} found`);
    } else {
      const empty = document.createElement('div');
      empty.className = 'snug-mobile-search-empty';
      empty.innerHTML = '<strong>No exact match yet.</strong><span>Try “sofa cover”, a colour or a material.</span>';
      results.appendChild(empty);
      setStatus('No products found');
    }

    const all = document.createElement('a');
    all.className = 'snug-mobile-search-view-all';
    all.href = `/search?type=product&q=${encodeURIComponent(normalizeSearchQuery(query))}`;
    all.textContent = `View all results for “${query}”`;
    results.appendChild(all);
  };

  const searchProducts = async (query) => {
    const term = query.trim();
    if (term.length < 2) {
      requestController?.abort();
      renderDefault();
      return;
    }

    requestController?.abort();
    requestController = new AbortController();
    setStatus('Searching products…');

    try {
      const endpoint = new URL('/search/suggest.json', window.location.origin);
      endpoint.searchParams.set('q', term);
      endpoint.searchParams.set('resources[type]', 'product');
      endpoint.searchParams.set('resources[limit]', '6');
      endpoint.searchParams.set('resources[options][unavailable_products]', 'last');
      const response = await fetch(endpoint, {
        signal: requestController.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      const payload = await response.json();
      renderProducts(payload.resources?.results?.products || [], term);
    } catch (error) {
      if (error.name === 'AbortError') return;
      results.replaceChildren();
      const fallback = document.createElement('a');
      fallback.className = 'snug-mobile-search-view-all';
      fallback.href = `/search?type=product&q=${encodeURIComponent(term)}`;
      fallback.textContent = `Search the store for “${term}”`;
      results.appendChild(fallback);
      setStatus('Press Search to view results');
    }
  };

  const closeSearch = ({ restoreFocus = true } = {}) => {
    if (!sheet || sheet.hidden) return;
    requestController?.abort();
    window.clearTimeout(debounceTimer);
    if (input) input.value = '';
    renderDefault();
    sheet.classList.remove('is-open');
    sheet.hidden = true;
    root.classList.remove('snug-mobile-search-active');
    document.body.classList.remove('snug-mobile-search-active');
    if (restoreFocus && lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
  };

  const ensureSheet = () => {
    if (sheet?.isConnected) return sheet;

    sheet = document.createElement('div');
    sheet.id = 'snug-mobile-search-sheet';
    sheet.className = 'snug-mobile-search-sheet';
    sheet.hidden = true;
    sheet.innerHTML = `
      <button class="snug-mobile-search-sheet__backdrop" type="button" aria-label="Close search"></button>
      <section class="snug-mobile-search-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="snug-mobile-search-title">
        <header class="snug-mobile-search-sheet__header">
          <div>
            <span>SNUG &amp; HOMELY SEARCH</span>
            <h2 id="snug-mobile-search-title">Find your perfect sofa cover</h2>
          </div>
          <button class="snug-mobile-search-sheet__close" type="button" aria-label="Close search">
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true"><path d="M4 4l12 12M16 4 4 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
        </header>
        <form class="snug-mobile-search-form" action="/search" method="get" role="search">
          <input type="hidden" name="type" value="product">
          <label class="snug-mobile-search-visually-hidden" for="snug-mobile-search-input">Search products</label>
          <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="m16.2 16.2 4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          <input id="snug-mobile-search-input" type="search" name="q" placeholder="Search sofa covers, colours or sizes" autocomplete="off" autocapitalize="off" spellcheck="false">
          <input type="hidden" name="options[unavailable_products]" value="last">
          <button type="submit">Search</button>
        </form>
        <p class="snug-mobile-search-status" role="status" aria-live="polite"></p>
        <div class="snug-mobile-search-results"></div>
      </section>`;
    document.body.appendChild(sheet);

    panel = sheet.querySelector('.snug-mobile-search-sheet__panel');
    input = sheet.querySelector('#snug-mobile-search-input');
    results = sheet.querySelector('.snug-mobile-search-results');
    status = sheet.querySelector('.snug-mobile-search-status');

    const form = sheet.querySelector('.snug-mobile-search-form');
    form.addEventListener('submit', () => {
      input.value = normalizeSearchQuery(input.value);
    }, true);

    sheet.querySelector('.snug-mobile-search-sheet__backdrop').addEventListener('click', () => {
      if (Date.now() - openedAt < 500) return;
      closeSearch();
    });
    sheet.querySelector('.snug-mobile-search-sheet__close').addEventListener('click', () => closeSearch());
    panel.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) closeSearch({ restoreFocus: false });
    });
    input.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => searchProducts(input.value), 220);
    });

    renderDefault();
    return sheet;
  };

  const openSearch = (trigger) => {
    if (!isMobile()) return;
    lastTrigger = trigger instanceof HTMLElement ? trigger : null;
    closeCartBeforeSearch();
    closeNativeSearch();
    ensureSheet();
    root.classList.add('snug-mobile-search-active');
    document.body.classList.add('snug-mobile-search-active');
    openedAt = Date.now();
    sheet.hidden = false;
    sheet.classList.add('is-open');
    if (!input.value.trim()) renderDefault();
    window.setTimeout(() => input.focus({ preventScroll: true }), 40);
  };

  const handleTrigger = (event) => {
    if (!isMobile()) return;
    const target = event.target;
    if (!(target instanceof Element) || target.closest('#snug-mobile-search-sheet')) return;
    const trigger = target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openSearch(trigger);
  };

  /* Opening on pointerdown can make the same tap land on the newly shown backdrop. */
  document.addEventListener('click', handleTrigger, true);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && sheet && !sheet.hidden) {
      event.preventDefault();
      closeSearch();
    }
  });

  mobileMedia.addEventListener?.('change', (event) => {
    if (!event.matches) closeSearch({ restoreFocus: false });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureSheet, { once: true });
  } else {
    ensureSheet();
  }
})();
